import { useSetRiskProfileMutation } from "Api/Mutations/User/useSetRiskProfileMutation";
import { Box, Button, Stack, Typography } from "@mui/material";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { useAdminUserDataQuery } from "Api/Queries/User/useAdminUserDataQuery";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { LegalEntityContent } from "Components/AML/AMLUserDetail/LegalEntityContent/LegalEntityContent";
import { NaturalPersonContent } from "Components/AML/AMLUserDetail/NaturalPersonContent/NaturalPersonContent";
import { SelfEmployedContent } from "Components/AML/AMLUserDetail/SelfEmployedContent/SelfEmployedContent";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ConnectedBankAccounts } from "Components/Users/ConnectedBankAccounts/ConnectedBankAccounts";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ExportTransactionsButton } from "Components/Shared/ExportTransactionsButton";
import { Dropdown } from "Components/Shared/Inputs/Dropdown";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { PartyForm } from "Components/Users/UserDetailPageContent/Components/PartyForm/PartyForm";
import { IdentityVerificationButton } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/IdentityVerificationButton";
import { useRiskProfileStatusList } from "Hooks/Options/useRiskProfileStatusList";
import { useIsCurrentUserAML } from "Hooks/useIsCurrentUserAml";
import {
  type PartyDto,
  PartyType,
  type RiskProfileStatus,
  type UserVerificationDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { type FC, type ReactNode, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

const COMPONENT_RESOURCES = Resources.Pages.Content.AmlUsers.Detail;
const PARTY_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party;

type ContentProps = {
  partyData: PartyDto;
  verificationData: UserVerificationDto;
};

const PARTY_TYPE_TO_CONTENT_MAP: Record<PartyType, FC<ContentProps>> = {
  [PartyType.LegalEntity]: LegalEntityContent,
  [PartyType.SelfEmployed]: SelfEmployedContent,
  [PartyType.NaturalPerson]: NaturalPersonContent,
};

export const AMLUserDetailPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const riskProfileList = useRiskProfileStatusList();
  const isAMLUser = useIsCurrentUserAML();
  const { id } = useParams();
  const { t } = useResource();

  const userID = Number(id);

  const { data: userDetailData, isLoading: isUserDetailDataLoading } =
    useAdminUserDataQuery(userID);

  const { data: verificationData, isLoading: isVerificationLoading } =
    useVerificationsQuery(userID);

  const { data: partyData, isLoading: isPartyLoading } = usePartyQuery(
    userDetailData?.partyPublicID,
  );

  const { mutate: updateRiskProfile, isPending } = useSetRiskProfileMutation({
    onSuccess: () => {
      toast.success(t(COMPONENT_RESOURCES.RiskProfileUpdateSuccess));
    },
    onError: () => toast.error(t(COMPONENT_RESOURCES.RiskProfileUpdateError)),
  });

  const isLoading =
    isPartyLoading || isUserDetailDataLoading || isVerificationLoading;
  const isError =
    !partyData || !verificationData || !userDetailData?.partyPublicID || !id;

  if (isLoading) {
    return (
      <Layout pageTitle="">
        <CenteredCircularProgress />
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout pageTitle={t(Resources.Error.Error)}>
        <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>
      </Layout>
    );
  }

  const { type, companyName, lastName, firstName, clientIdentifier } =
    partyData;
  const pageTitle =
    companyName ||
    (lastName ? `${firstName} ${lastName}` : t(Resources.Common.User));

  const ContentComponent = PARTY_TYPE_TO_CONTENT_MAP[type];

  if (isEditing) {
    return (
      <Layout pageTitle={pageTitle} isWithoutPaper>
        <PartyForm
          party={partyData}
          onCancel={() => setIsEditing(false)}
          onSaved={() => setIsEditing(false)}
        />
      </Layout>
    );
  }

  return (
    <Layout pageTitle={pageTitle}>
      <Box display="flex" gap={1} justifyContent="flex-end">
        <IdentityVerificationButton
          userID={userID}
          currentStatus={verificationData.identityVerificationStatus}
          isDisabled={
            partyData.type !== PartyType.LegalEntity &&
            !partyData.hasIdentificationsUploaded
          }
          disabledTooltip={t(PARTY_RESOURCES.NoDocumentsUploaded)}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => setIsEditing(true)}
        >
          {t(Resources.Common.Edit)}
        </Button>
      </Box>
      <Stack gap={8}>
        {clientIdentifier && (
          <Box>
            <Typography fontWeight={400}>
              {t(COMPONENT_RESOURCES.ClientIdentifier)}:
            </Typography>
            <Box ml={3} display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={500}>
                {clientIdentifier}
              </Typography>
              <CopyToClipboard
                text={clientIdentifier}
                accessibilityLabel={t(COMPONENT_RESOURCES.CopyClientIdentifier)}
                successMessage={t(
                  COMPONENT_RESOURCES.CopyClientIdentifierSuccess,
                )}
              />
            </Box>
          </Box>
        )}
        <ContentComponent
          partyData={partyData}
          verificationData={verificationData}
        />
        <ValuesBox title={t(COMPONENT_RESOURCES.ConnectedBankAccounts)}>
          <ConnectedBankAccounts
            userID={userID}
            verifiedIBAN={userDetailData.verifiedIBAN}
            bankVerificationSource={userDetailData.bankVerificationSource}
            bankVerificationDate={userDetailData.bankVerificationDate}
            firstName={firstName}
            lastName={lastName}
            companyName={companyName}
          />
          <ExportTransactionsButton
            fileNamePrefix={`${t(Resources.Components.Transactions.Title)}_${pageTitle}`}
            userID={userID}
          />
        </ValuesBox>
        <ValuesBox title={t(COMPONENT_RESOURCES.RiskProfileSettings)}>
          <Dropdown
            name="riskProfile"
            value={verificationData.riskProfile}
            onChange={(riskProfile) =>
              updateRiskProfile({
                targetUserID: userID,
                riskProfile: riskProfile as RiskProfileStatus,
              })
            }
            options={riskProfileList.map(({ name, code }) => ({
              Name: name,
              Value: code,
            }))}
            disabled={isPending || !isAMLUser}
          />
        </ValuesBox>
      </Stack>
    </Layout>
  );
};

type LayoutProps = {
  children: ReactNode;
  pageTitle: string;
  isWithoutPaper?: boolean;
};

const Layout = ({ children, pageTitle, isWithoutPaper }: LayoutProps) => (
  <AuthenticatedLayout
    title={pageTitle}
    backNavigation={{
      mode: "historyStack",
      fallbackUrl: appUrl("aml-users"),
    }}
  >
    <StyledPageWrapper>
      <div className="left-content">
        {isWithoutPaper ? children : <PrimaryPaper>{children}</PrimaryPaper>}
      </div>
    </StyledPageWrapper>
  </AuthenticatedLayout>
);
